import { z } from 'zod';
import { HexColorSchema, TimestampSchema } from '../common';
import { EventId, PlayerId, UserId } from '../ids';
import { PlayerSchema } from '../player';

export const PlayerCreatedEventSchema = z.object({
  id: EventId,
  timestamp: TimestampSchema,
  userId: UserId.optional(),
  type: z.literal('PlayerCreated'),
  payload: z.object({
    playerId: PlayerId,
    name: PlayerSchema.shape.name,
    nickname: PlayerSchema.shape.nickname,
    handle: PlayerSchema.shape.handle,
    color: HexColorSchema.optional(),
  }),
});
export type PlayerCreatedEvent = z.infer<typeof PlayerCreatedEventSchema>;

export const PlayerUpdatedEventSchema = z.object({
  id: EventId,
  timestamp: TimestampSchema,
  userId: UserId.optional(),
  type: z.literal('PlayerUpdated'),
  payload: z.object({
    playerId: PlayerId,
    name: PlayerSchema.shape.name.optional(),
    nickname: PlayerSchema.shape.nickname,
    handle: PlayerSchema.shape.handle,
    color: HexColorSchema.optional(),
  }),
});
export type PlayerUpdatedEvent = z.infer<typeof PlayerUpdatedEventSchema>;

export const PlayerMergedEventSchema = z.object({
  id: EventId,
  timestamp: TimestampSchema,
  userId: UserId.optional(),
  type: z.literal('PlayerMerged'),
  payload: z.object({
    sourcePlayerId: PlayerId,
    targetPlayerId: PlayerId,
  }),
});
export type PlayerMergedEvent = z.infer<typeof PlayerMergedEventSchema>;

export const PlayerUnmergedEventSchema = z.object({
  id: EventId,
  timestamp: TimestampSchema,
  userId: UserId.optional(),
  type: z.literal('PlayerUnmerged'),
  payload: z.object({
    playerId: PlayerId,
  }),
});
export type PlayerUnmergedEvent = z.infer<typeof PlayerUnmergedEventSchema>;

export const PlayerDeletedEventSchema = z.object({
  id: EventId,
  timestamp: TimestampSchema,
  userId: UserId.optional(),
  type: z.literal('PlayerDeleted'),
  payload: z.object({
    playerId: PlayerId,
  }),
});
export type PlayerDeletedEvent = z.infer<typeof PlayerDeletedEventSchema>;

export const PlayerRestoredEventSchema = z.object({
  id: EventId,
  timestamp: TimestampSchema,
  userId: UserId.optional(),
  type: z.literal('PlayerRestored'),
  payload: z.object({
    playerId: PlayerId,
  }),
});
export type PlayerRestoredEvent = z.infer<typeof PlayerRestoredEventSchema>;

export const PlayerClaimedEventSchema = z.object({
  id: EventId,
  timestamp: TimestampSchema,
  userId: UserId.optional(),
  type: z.literal('PlayerClaimed'),
  payload: z.object({
    playerId: PlayerId,
    handle: PlayerSchema.shape.handle, // Optional handle set during claim
  }),
});
export type PlayerClaimedEvent = z.infer<typeof PlayerClaimedEventSchema>;

export const PlayerUnclaimedEventSchema = z.object({
  id: EventId,
  timestamp: TimestampSchema,
  userId: UserId.optional(),
  type: z.literal('PlayerUnclaimed'),
  payload: z.object({
    playerId: PlayerId,
  }),
});
export type PlayerUnclaimedEvent = z.infer<typeof PlayerUnclaimedEventSchema>;

export const PlayerEventSchema = z.discriminatedUnion('type', [
  PlayerCreatedEventSchema,
  PlayerUpdatedEventSchema,
  PlayerMergedEventSchema,
  PlayerUnmergedEventSchema,
  PlayerDeletedEventSchema,
  PlayerRestoredEventSchema,
  PlayerClaimedEventSchema,
  PlayerUnclaimedEventSchema,
]);
export type PlayerEvent = z.infer<typeof PlayerEventSchema>;
